import { Directive, inject, output } from '@angular/core';
import {
  AuctionService,
  ConfigurationState,
  InventoryManagementAuctionItemStatus,
  InventoryManagementSearchResult,
  InventoryManagementUpdateItemArrivedMultiple,
  InventoryManagementUpdateItemStatusMultiple,
  LaunchDarklyService,
  ReservationResponseDto,
  VehicleToggleState
} from 'remarketing-angular-library';
import { select, Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { get } from 'lodash';
import { SaComponent, SaDirective } from 'smartauction-frontend-micro-library';

@Directive({
  selector: '[appChangeStatusModal]',
})
export abstract class ChangeStatusModalDirective extends SaDirective {

  protected readonly configuration$ = select(ConfigurationState.configuration);

  public readonly close = output();
  public readonly processing = output<string>();
  public readonly arrived = output();
  public readonly error = output<string>();
  public readonly message = output<Map<string,string[]>>();

  protected messages: Map<string,string[]> = new Map<string,string[]>();
  protected errorMap: Map<string,string[]> = new Map<string,string[]>();
  protected successVehicles: string[] = [];
  protected readonly inventoryManagementAuctionItemStatus = InventoryManagementAuctionItemStatus;

  protected changeStatusModalTranslations = {
    areAvailableForSale: '',
    isAvailableForSale: '',
    itExceedsPostingLimit: '',
    theyExceedPostingLimit: '',
    isMissingField: '',
    areMissingFields: '',
    singleLeadingBid: '',
    pluralLeadingBids: '',
    isInRequestedStatus: '',
    areInRequestedStatus: '',
    vehicleStatusHasChanged: '',
    alreadySold: '',
    weCantFind: '',
    inTheDatabase: '',
    cannotCompleteRequest: '',
    weCantMove: '',
    holdToActive: '',
    singleVehicleOnHold: '',
    pluralVehicleOnHold: '',
    isRemovedFromAuction: '',
    areRemovedFromAuction: '',
    singleVehicleRemoved: '',
    pluralVehiclesRemoved: '',
    isNoLongerListed: '',
    areNoLongerListed: '',
    singleVehicleActive: '',
    pluralVehicleActive: '',
    weCantList: '',
    cantSaveChange: '',
    and: '',
    singleVehicleReserved: '',
    pluralVehicleReserved: '',
    invalidVehicleType: '',
    invalidAuctionItemStatus: '',
    invalidBuyerForSales: '',
    vehicleReservedForDifferentBuyer: '',
    isReserved: '',
    areReserved: '',
    isUnreserved: '',
    areUnreserved: '',
    vehicleReservedByDifferentUser: '',
    noVehiclesReserved: '',
    failedDueTo: '',
    limitExceeds: '',
    tsrMemoryIssue: '',
    vehicleOnActiveInServiceList: ''
  };

  protected readonly store: Store;
  protected readonly auctionService: AuctionService;
  protected readonly _launchDarklyService: LaunchDarklyService;
  protected readonly translocoService: TranslocoService;

  protected constructor(
  ) {
    super();
    this.store = inject(Store);
    this.auctionService = inject(AuctionService);
    this._launchDarklyService = inject(LaunchDarklyService);
    this.translocoService = inject(TranslocoService);
  }

  get isAdmin() {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  get selectedVehicles() {
    return this.store.selectSnapshot(VehicleToggleState.selectedVehicles) as InventoryManagementSearchResult[];
  }

  get selectedVehiclesStatus() {
    return this.selectedVehicles.length > 0 ? this.selectedVehicles[0].auctionItemStatus : undefined;
  }

  get bidSelectedVehicles() {
    const selected = this.selectedVehicles;
    return selected.filter(i => i.bidCount > 0);
  }

  get offerSelectedVehicles() {
    const selected = this.selectedVehicles;
    return selected.filter(i => i.openOfferCount > 0);
  }

  get nonBidSelectedVehicles() {
    const selected = this.selectedVehicles;
    return selected.filter(i => i.bidCount === 0);
  }

  get offerExistWarning() {
    return this.offerSelectedVehicles.length > 0;
  }

  get bidExistWarning() {
    return this.bidSelectedVehicles.length > 0;
  }

  vinNumberText(selectedVehicles) {
    let vinText = '';
    const selected = selectedVehicles;
    if (selected.length === 1) {
      vinText = selected[0].itemDetail.vin;
    } else {
      selected.forEach((v, index) => {
        if (index < selected.length - 1) {
          vinText += `${v.itemDetail.vin}, `;
        } else {
          vinText += `${this.changeStatusModalTranslations.and} ${v.itemDetail.vin}`;
        }
      });
    }
    return vinText;
  }

  changeStatus(status: InventoryManagementAuctionItemStatus) {
    const auctionItemUuids = [];
    this.errorMap = new Map<string,string[]>();
    this.successVehicles = [];
    this.selectedVehicles.forEach(v => {
      auctionItemUuids.push(v.vehicleUuid);
    });

    // Change vehicle status
    this.auctionService.updateVehicleStatus(auctionItemUuids, status)
    .subscribe(response => {
      const allSuccess = true;
      let error;
      let updatedVehicles = [...this.selectedVehicles];
      ({ updatedVehicles } = this.selectedVehiclesCaptureErrorSuccess(response, updatedVehicles, allSuccess, error));

      // Only display our processing modal if all updates were successful
      if (this.successVehicles.length > 0) {
        this.setSuccessMessages(status);
      }
      this.setErrorMessages();
      this.message.emit(this.messages);

      if (updatedVehicles.length > 0) {
        this.store.dispatch(new InventoryManagementUpdateItemStatusMultiple(updatedVehicles, status));
      }
    });
  }

  changeReserveStatus(status: InventoryManagementAuctionItemStatus, response: ReservationResponseDto[]) {
    const auctionItemUuids = [];
    this.errorMap = new Map<string,string[]>();
    this.successVehicles = [];
    this.selectedVehicles.forEach(v => {
      auctionItemUuids.push(v.vehicleUuid);
    });

    const allSuccess = true;
    let error;
    const updatedVehicles = [...this.selectedVehicles];
    this.selectedVehiclesCaptureErrorSuccess(response, updatedVehicles, allSuccess, error);

    // Only display our processing modal if all updates were successful
    if (this.successVehicles.length > 0) {
      this.setSuccessMessages(status);
    }
    this.setErrorMessages();

    if (status === 'RESERVED') {
      let paiErrorCount = 0;
      response.forEach(vehicle => {
        if (vehicle.errors && vehicle.errors.length > 0) {
          vehicle.success = false;
          if (vehicle['errors'][0].errorMessage.includes('PAI')) {
            paiErrorCount++;
          }
        }
      });

      const reservedCount = response.length - paiErrorCount;
      let header;
      const headerArray: string[] = [];
      if (reservedCount < 1){
        header = this.changeStatusModalTranslations.noVehiclesReserved;
      } else {
        header = `${reservedCount} ${reservedCount > 1
          ? this.changeStatusModalTranslations.pluralVehicleReserved
          : this.changeStatusModalTranslations.singleVehicleReserved}`;
      }

      headerArray.push(header);
      this.messages.set('header', headerArray);
    }
    this.message.emit(this.messages);
  }

  private vehiclesSelected(response: any, updatedVehicles: InventoryManagementSearchResult[], allSuccess: boolean, error: any) {
    response.forEach(r => {
      if (!r.success) {
        // Remove any failed updates from our array, so that they don't get the processing banner
        updatedVehicles = updatedVehicles.filter(x => x.vehicleUuid !== r.auctionItemUuid);
        // If there are any errors, only display the first
        if (allSuccess) {
          allSuccess = false;
          error = r.errors[0];
          this.errorMap.set(r.auctionItemUuid, error);
        }
      }

    });
    return { updatedVehicles, allSuccess, error };
  }

  private selectedVehiclesCaptureErrorSuccess(response: any, updatedVehicles: InventoryManagementSearchResult[], allSuccess: boolean, error: any) {
    response.forEach(r => {
      if (!r.success ||  r.errors?.length > 0) {
        // Remove any failed updates from our array, so that they don't get the processing banner
        updatedVehicles = updatedVehicles.filter(x => x.vehicleUuid !== r.auctionItemUuid);
        const error = r.errors[0].errorCode || r.errors[0].errorMessage;
        // If there are any errors, only display the first
        if (this.errorMap.has(error)) {
          const auctionItemArray = this.errorMap.get(error);
          auctionItemArray.push(r.auctionItemUuid);
          this.errorMap.set(error, auctionItemArray);
        } else {
          const auctionItemArray: string[] = [];
          auctionItemArray.push(r.auctionItemUuid);
          this.errorMap.set(error, auctionItemArray);
        }
        // if the error mapping was entered due to the or clause of r.errors.length > 0, but still a success
        // Avis special reservation case  see (SAT-73526)
        if (r.success) {
          this.successVehicles.push(r.auctionItemUuid);
        }

      } else {
        this.successVehicles.push(r.auctionItemUuid);
      }

    });
    return { updatedVehicles };
  }

  markVehicleArrived() {
    const auctionItemUuids = [];
    this.selectedVehicles.forEach(v => {
      auctionItemUuids.push(v.vehicleUuid);
    });
    // Change vehicle status
    this.auctionService.markVehicleArrived(auctionItemUuids)
    .subscribe(response => {
      let allSuccess = true;
      let error;
      let updatedVehicles = [...this.selectedVehicles];
      ({ updatedVehicles, allSuccess, error } = this.vehiclesSelected(response, updatedVehicles, allSuccess, error));

      // Only display our processing modal if all updates were successful
      if (allSuccess) {
        this.arrived.emit();
      } else {
        this.openErrorModalForArrived(error);
      }
      if (updatedVehicles.length > 0) {
        this.store.dispatch(new InventoryManagementUpdateItemArrivedMultiple(updatedVehicles));
      }
    });
  }

  setErrorMessages() {
    const errorMessages: string[] = [];
    this.errorMap.forEach((erroredAuctionItems, errorCode) => {
      const erroredVehicles = this.selectedVehicles.filter(v => erroredAuctionItems.includes(v.vehicleUuid));
      errorMessages.push(this.getErrorMessageByCodeAndVin(errorCode, erroredVehicles));
    });
    this.messages.set('error', errorMessages);
  }

  getErrorMessageByCodeAndVin(errorCode: string, erroredVehicles) {
    const vin = this.getErroredVehiclesVin(erroredVehicles);
    let message = '';
    switch (errorCode) {
      case 'AUCTION_ITEM_CANNOT_MOVED_TO_ACTIVE':
        message = `${this.changeStatusModalTranslations.weCantMove} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.holdToActive}`;
        break;
      case 'INSUFFICIENT_SELLING_CREDIT_LIMIT':
        message = this.getExceedsPostLimitErrorMessage(erroredVehicles, message);
        break;
      case 'GENERAL_ERROR':
        message = `${this.changeStatusModalTranslations.cannotCompleteRequest}`;
        break;
      case 'AUCTION_ITEM_NOT_FOUND':
      case 'AINF':
        message = `${this.changeStatusModalTranslations.weCantFind} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.inTheDatabase}`;
        break;
      case 'MISSING_MANDATORY_ATTRIBUTES':
        message = this.getRequiredFieldsErrorMessage(erroredVehicles, message);
        break;
      case 'AUCTION_ITEM_SOLD':
        message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.alreadySold}`;
        break;
      case 'AUCTION_ITEM_IN_SAME_STATUS':
      case 'VAR':
      case 'VAUR':
        message = this.getSameStatusErrorMessage(erroredVehicles, message);
        break;
      case 'AUCTION_ITEM_STATUS_ALREADY_CHANGED':
        message = `${this.changeStatusModalTranslations.vehicleStatusHasChanged}`;
        break;
      case 'LBE':
        message = this.getLeadingBidExistingErrorMessage(erroredVehicles, message);
        break;
      case 'IVT':
        message = `${this.changeStatusModalTranslations.invalidVehicleType} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'IAIS':
        message = `${this.changeStatusModalTranslations.invalidAuctionItemStatus} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'BNAFSP':
        message = `${this.changeStatusModalTranslations.invalidAuctionItemStatus} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'VARFDB':
        message = `${this.changeStatusModalTranslations.vehicleReservedForDifferentBuyer} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'VRBDU':
        message = `${this.changeStatusModalTranslations.vehicleReservedByDifferentUser} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'EWUL':
        message = `${this.changeStatusModalTranslations.limitExceeds} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>`;
        break;
      case 'TSR_MEMORY_ISSUE':
        message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)}</strong> ${this.changeStatusModalTranslations.failedDueTo} ${this.changeStatusModalTranslations.tsrMemoryIssue}`;
        break;
      case 'VCBSOLE':
        message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)}</strong> ${this.changeStatusModalTranslations.vehicleOnActiveInServiceList}`;
        break;
      default:
        message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)}</strong> ${this.changeStatusModalTranslations.failedDueTo} ${errorCode}`;
        break;
    }
    return message;
  }

  private getErroredVehiclesVin(erroredVehicles: any) {
    return erroredVehicles.length > 1 ? '<strong>VINs</strong>' : '<strong>VIN</strong>';
  }

  private getSameStatusErrorMessage(erroredVehicles: any, message: string) {
    const vin = this.getErroredVehiclesVin(erroredVehicles);
    if (erroredVehicles.length > 1) {
      message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.areInRequestedStatus}`;
    } else {
      message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.isInRequestedStatus}`;
    }
    return message;
  }

  private getLeadingBidExistingErrorMessage(erroredVehicles: any, message: string) {
    const vin = this.getErroredVehiclesVin(erroredVehicles);
    if (erroredVehicles.length > 1) {
      message = `${this.changeStatusModalTranslations.pluralLeadingBids} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>.`;
    } else {
      message = `${this.changeStatusModalTranslations.singleLeadingBid} ${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>.`;
    }
    return message;
  }

  private getRequiredFieldsErrorMessage(erroredVehicles: any, message: string) {
    const vin = this.getErroredVehiclesVin(erroredVehicles);
    if (erroredVehicles.length > 1) {
      message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.areMissingFields}`;
    } else {
      message = `${vin}<strong> ${this.vinNumberText(erroredVehicles)} </strong>${this.changeStatusModalTranslations.isMissingField}`;
    }
    return message;
  }

  private getExceedsPostLimitErrorMessage(erroredVehicles: any, message: string) {
    const vin = this.getErroredVehiclesVin(erroredVehicles);
    if (erroredVehicles.length > 1) {
      message = `${this.changeStatusModalTranslations.weCantList} ${vin} <strong>${this.vinNumberText(erroredVehicles)}</strong> ${this.changeStatusModalTranslations.itExceedsPostingLimit}`;
    } else {
      message = `${this.changeStatusModalTranslations.weCantList} ${vin} <strong>${this.vinNumberText(erroredVehicles)}</strong> ${this.changeStatusModalTranslations.theyExceedPostingLimit}`;
    }
    return message;
  }

  openErrorModalForArrived(error: any) {
    if (error.errorCode === 'AUCTION_ITEM_IN_SAME_STATUS') {
      this.error.emit(`${this.changeStatusModalTranslations.cantSaveChange}`);
    }
    else {
      this.error.emit(error.errorMessage);
    }
  }

  get isChangeReservedDealershipEnabled() {
    return this._launchDarklyService.getFeatureFlag('sell-changeReservedDealerEnabled', false);
  }

  setSuccessMessages(status: InventoryManagementAuctionItemStatus) {
    const successVehicles = this.selectedVehicles.filter(v => this.successVehicles.includes(v.vehicleUuid));
    const messageArray: string[] = [];
    const headerArray: string[] = [];
    if (this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.ACTIVE && status === InventoryManagementAuctionItemStatus.HOLD) {
      this.generateSuccessMsgForActiveToHold(successVehicles, messageArray, headerArray);
    } else if ((this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.ACTIVE && status === InventoryManagementAuctionItemStatus.REMOVED) ||
      (this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.HOLD && status === InventoryManagementAuctionItemStatus.REMOVED)) {
      this.generateSuccessMsgForActiveOrHoldToRemoved(successVehicles, messageArray, headerArray);
    } else if (this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.HOLD && status === InventoryManagementAuctionItemStatus.ACTIVE) {
      this.generateSuccessMsgForHoldToActive(successVehicles, messageArray, headerArray);
    } else if ((this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.ACTIVE || (this.isChangeReservedDealershipEnabled && this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.RESERVED))
      && status === InventoryManagementAuctionItemStatus.RESERVED) {
      this.generateSuccessMsgForStatusToReserved(successVehicles, messageArray);
    } else if (this.selectedVehiclesStatus === InventoryManagementAuctionItemStatus.RESERVED  && status === InventoryManagementAuctionItemStatus.ACTIVE) {
      this.generateSuccessMsgForReservedToActive(successVehicles, messageArray, headerArray);
    }
  }

  private generateSuccessMsgForStatusToReserved(successVehicles: InventoryManagementSearchResult[], messageArray: string[]) {
    const vin = this.successVehicleslength(successVehicles);
    const message = `${vin}<strong> ${this.vinNumberText(successVehicles)}</strong>
      ${successVehicles.length > 1 ? this.changeStatusModalTranslations.areReserved : this.changeStatusModalTranslations.isReserved}`;
    messageArray.push(message);
    this.messages.set('success', messageArray);
  }

  private generateSuccessMsgForReservedToActive(successVehicles: InventoryManagementSearchResult[], messageArray: string[], headerArray: string[]) {
    const vin = this.successVehicleslength(successVehicles);
    let message = '';
    let header = '';
    if (successVehicles.length > 1) {
      message = `${vin}<strong> ${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.areUnreserved}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.pluralVehicleActive}`;
    } else {
      message = `${vin}<strong> ${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.isUnreserved}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.singleVehicleActive}`;
    }

    messageArray.push(message);
    this.messages.set('success', messageArray);
    headerArray.push(header);
    this.messages.set('header', headerArray);
  }

  private generateSuccessMsgForHoldToActive(successVehicles: InventoryManagementSearchResult[], messageArray: string[], headerArray: string[]) {
    const vin = this.successVehicleslength(successVehicles);
    let message = '';
    let header = '';
    if (successVehicles.length > 1) {
      message = `${vin}<strong> ${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.areAvailableForSale}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.pluralVehicleActive}`;
    } else {
      message = `${vin}<strong> ${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.isAvailableForSale}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.singleVehicleActive}`;
    }

    messageArray.push(message);
    this.messages.set('success', messageArray);
    headerArray.push(header);
    this.messages.set('header', headerArray);
  }

  private successVehicleslength(successVehicles: InventoryManagementSearchResult[]) {
    return successVehicles.length > 1 ? '<strong>VINs</strong>' : '<strong>VIN</strong>';
  }

  private generateSuccessMsgForActiveOrHoldToRemoved(successVehicles: InventoryManagementSearchResult[], messageArray: string[], headerArray: string[]) {
    const vin = this.successVehicleslength(successVehicles);
    let message = '';
    let header = '';
    if (successVehicles.length > 1) {
      message = `${vin} <strong>${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.areNoLongerListed}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.pluralVehiclesRemoved}`;
    } else {
      message = `${vin} <strong>${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.isNoLongerListed}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.singleVehicleRemoved}`;
    }
    messageArray.push(message);
    this.messages.set('success', messageArray);
    headerArray.push(header);
    this.messages.set('header', headerArray);
  }

  private generateSuccessMsgForActiveToHold(successVehicles: InventoryManagementSearchResult[], messageArray: string[], headerArray: string[]) {
    const vin = this.successVehicleslength(successVehicles);
    let message = '';
    let header = '';
    if (successVehicles.length > 1) {
      message = `${vin} <strong>${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.areRemovedFromAuction}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.pluralVehicleOnHold}`;
    } else {
      message = `${vin} <strong>${this.vinNumberText(successVehicles)}</strong> ${this.changeStatusModalTranslations.isRemovedFromAuction}`;
      header = `${successVehicles.length} ${this.changeStatusModalTranslations.singleVehicleOnHold}`;
    }
    messageArray.push(message);
    this.messages.set('success', messageArray);
    headerArray.push(header);
    this.messages.set('header', headerArray);
  }

  closeModal() {
    this.close.emit();
  }

  getTranslations() {
    const keyParams = {
      'sell.changeStatusModals': {},
      common: {}
    };

    this.translocoService.selectTranslateObject(keyParams).pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.changeStatusModalTranslations.areAvailableForSale = translate[0].areAvailableForSale;
      this.changeStatusModalTranslations.isAvailableForSale = translate[0].isAvailableForSale;
      this.changeStatusModalTranslations.itExceedsPostingLimit = translate[0].itExceedsPostingLimit;
      this.changeStatusModalTranslations.theyExceedPostingLimit = translate[0].theyExceedPostingLimit;
      this.changeStatusModalTranslations.isMissingField = translate[0].isMissingField;
      this.changeStatusModalTranslations.areMissingFields = translate[0].areMissingFields;
      this.changeStatusModalTranslations.singleLeadingBid = translate[0].singleLeadingBid;
      this.changeStatusModalTranslations.pluralLeadingBids = translate[0].pluralLeadingBids;
      this.changeStatusModalTranslations.isInRequestedStatus = translate[0].isInRequestedStatus;
      this.changeStatusModalTranslations.areInRequestedStatus = translate[0].areInRequestedStatus;
      this.changeStatusModalTranslations.vehicleStatusHasChanged = translate[0].vehicleStatusHasChanged;
      this.changeStatusModalTranslations.alreadySold = translate[0].alreadySold;
      this.changeStatusModalTranslations.weCantFind = translate[0].weCantFind;
      this.changeStatusModalTranslations.inTheDatabase = translate[0].inTheDatabase;
      this.changeStatusModalTranslations.cannotCompleteRequest = translate[0].cannotCompleteRequest;
      this.changeStatusModalTranslations.weCantMove = translate[0].weCantMove;
      this.changeStatusModalTranslations.holdToActive = translate[0].holdToActive;
      this.changeStatusModalTranslations.singleVehicleOnHold = translate[0].singleVehicleOnHold;
      this.changeStatusModalTranslations.pluralVehicleOnHold = translate[0].pluralVehicleOnHold;
      this.changeStatusModalTranslations.isRemovedFromAuction = translate[0].isRemovedFromAuction;
      this.changeStatusModalTranslations.areRemovedFromAuction = translate[0].areRemovedFromAuction;
      this.changeStatusModalTranslations.singleVehicleRemoved = translate[0].singleVehicleRemoved;
      this.changeStatusModalTranslations.pluralVehiclesRemoved = translate[0].pluralVehiclesRemoved;
      this.changeStatusModalTranslations.isNoLongerListed = translate[0].isNoLongerListed;
      this.changeStatusModalTranslations.areNoLongerListed = translate[0].areNoLongerListed;
      this.changeStatusModalTranslations.singleVehicleActive = translate[0].singleVehicleActive;
      this.changeStatusModalTranslations.pluralVehicleActive = translate[0].pluralVehicleActive;
      this.changeStatusModalTranslations.weCantList = translate[0].weCantList;
      this.changeStatusModalTranslations.cantSaveChange = translate[0].cantSaveChange;
      this.changeStatusModalTranslations.and = translate[1].and;
      this.changeStatusModalTranslations.singleVehicleReserved = translate[0].singleVehicleReserved;
      this.changeStatusModalTranslations.pluralVehicleReserved = translate[0].pluralVehicleReserved;
      this.changeStatusModalTranslations.invalidAuctionItemStatus = translate[0].invalidAuctionItemStatus;
      this.changeStatusModalTranslations.invalidVehicleType = translate[0].invalidVehicleType;
      this.changeStatusModalTranslations.invalidBuyerForSales = translate[0].invalidBuyerForSales;
      this.changeStatusModalTranslations.vehicleReservedForDifferentBuyer = translate[0].vehicleReservedForDifferentBuyer;
      this.changeStatusModalTranslations.isReserved = translate[0].isReserved;
      this.changeStatusModalTranslations.areReserved = translate[0].areReserved;
      this.changeStatusModalTranslations.isUnreserved = translate[0].isUnreserved;
      this.changeStatusModalTranslations.areUnreserved = translate[0].areUnreserved;
      this.changeStatusModalTranslations.vehicleReservedByDifferentUser = translate[0].vehicleReservedByDifferentUser;
      this.changeStatusModalTranslations.noVehiclesReserved = translate[0].noVehiclesReserved;
      this.changeStatusModalTranslations.failedDueTo = translate[0].failedDueTo;
      this.changeStatusModalTranslations.limitExceeds = translate[0].limitExceeds;
      this.changeStatusModalTranslations.tsrMemoryIssue = translate[0].tsrMemoryIssue;
      this.changeStatusModalTranslations.vehicleOnActiveInServiceList = translate[0].vehicleOnActiveInServiceList;
    });
  }
}
